import { ApiMetric } from "@incendium/api";
import { Typography } from "@mui/material";
import AnimatedList from "Components/AnimatedList/AnimatedList";
import { AccordianChartBuilderSidebarBlock } from "./ChartBuilderSidebarBlock";
import produce from "immer";
import { CallbackOrVal, IChart } from "Interfaces";
import { useCallback, useEffect, useMemo } from "react";
import { MetricSelector } from "features/analytics";

const AdditionalMetricRow = ({
  row,
  onEdit,
  rowProps,
}: {
  row: ApiMetric;
  onEdit: (v: ApiMetric) => void;
  rowProps?: { taken: ApiMetric[] };
}) => {
  const onChange = useCallback(
    (_, v) => {
      if (v?.metric) {
        onEdit(v.metric);
      }
    },
    [onEdit],
  );

  const filterFn = useCallback(
    (m) => !(rowProps?.taken || []).includes(m.metric),
    [rowProps?.taken],
  );

  return (
    <MetricSelector
      metric={row}
      onChange={onChange}
      fullWidth
      filterFn={filterFn}
      hasControls={false}
    />
  );
};

interface IChartBuilderAvailableMetricsProps {
  chart: IChart;
  setChart: (v: CallbackOrVal<IChart>) => void;
  y?: string;
}

function ChartBuilderAvailableMetrics({
  chart,
  setChart,
  y = "l",
}: IChartBuilderAvailableMetricsProps) {
  const primaryFields = useMemo(() => {
    const yAxis = chart.yAxisKeys.find((v: any) => v.key === y) as
      | { fields?: ApiMetric[] }
      | undefined;
    return ((yAxis?.fields || []) as ApiMetric[]).filter(
      (m) => m && m !== ApiMetric.METRIC_NOT_SET,
    );
  }, [chart.yAxisKeys, y]);

  const addedAvailableMetrics = useMemo(() => {
    return (chart.displayOptions?.availableMetrics || []).filter(
      (m) => !primaryFields.includes(m),
    );
  }, [chart, primaryFields]);

  const onAdd = useCallback(() => {
    setChart((chart) =>
      produce(chart, (draft) => {
        if (!draft.displayOptions) {
          draft.displayOptions = {};
        }

        if (!draft.displayOptions.availableMetrics) {
          draft.displayOptions.availableMetrics = [];
        }

        if (draft.displayOptions.availableMetrics.length === 0) {
          const yAxis = draft.yAxisKeys.find((v: any) => v.key === y) as
            | { fields?: ApiMetric[] }
            | undefined;
          draft.displayOptions.availableMetrics = (
            (yAxis?.fields || []) as ApiMetric[]
          ).filter((m) => m && m !== ApiMetric.METRIC_NOT_SET);
        }
        draft.displayOptions.availableMetrics.push(ApiMetric.METRIC_NOT_SET);
      }),
    );
  }, [setChart, y]);

  const onRemove = useCallback(
    (_idx: number, value: ApiMetric) => {
      setChart((chart) =>
        produce(chart, (draft) => {
          const index = (
            draft.displayOptions?.availableMetrics || []
          ).findIndex((m) => m === value);

          if (index >= 0) {
            draft.displayOptions?.availableMetrics?.splice(index, 1);
          }
        }),
      );
    },
    [setChart],
  );

  const onEdit = useCallback(
    (idx: number) => (value: ApiMetric) => {
      setChart((chart) =>
        produce(chart, (draft) => {
          // the idx here is after removing set metrics, need to calc real index
          const originalValue = addedAvailableMetrics[idx];
          const index = (
            draft.displayOptions!.availableMetrics || []
          ).findIndex((m) => m === originalValue);

          if (index >= 0) {
            draft.displayOptions!.availableMetrics![index] = value;
          }
        }),
      );
    },
    [setChart, addedAvailableMetrics],
  );

  useEffect(() => {
    if (
      addedAvailableMetrics.length === 0 &&
      (chart.displayOptions?.availableMetrics || []).length > 0
    ) {
      setChart((chart) =>
        produce(chart, (draft) => {
          draft.displayOptions!.availableMetrics = [];
        }),
      );
      return;
    }

    const available = chart.displayOptions?.availableMetrics || [];
    if (available.length === 0) {
      return;
    }

    // Keep all y-axis metrics present (and first) whenever availableMetrics is set
    const additionals = available.filter((m) => !primaryFields.includes(m));
    const next = [...primaryFields, ...additionals];
    const unchanged =
      next.length === available.length &&
      next.every((m, i) => m === available[i]);

    if (unchanged) {
      return;
    }

    setChart((chart) =>
      produce(chart, (draft) => {
        draft.displayOptions!.availableMetrics = next;
      }),
    );
  }, [
    addedAvailableMetrics,
    chart.displayOptions?.availableMetrics,
    primaryFields,
    setChart,
  ]);

  const disabledAddMore = useMemo(() => {
    return (chart.displayOptions?.availableMetrics || []).includes(
      ApiMetric.METRIC_NOT_SET,
    );
  }, [chart.displayOptions?.availableMetrics]);

  const selectableMetrics = useMemo(() => {
    return chart.displayOptions?.availableMetrics || [];
  }, [chart.displayOptions?.availableMetrics]);

  return (
    <AccordianChartBuilderSidebarBlock
      title={`Optional Metrics`}
      subTitle={
        <>
          <Typography variant="body2">
            Use this feature to allow chart viewers to dynamically switch
            between selected metrics.
          </Typography>
          <Typography variant="body2" fontStyle={"italic"}>
            Note that enabling this option adds a metric dropdown to the top
            right of the chart.
          </Typography>
        </>
      }
      defaultClosed={
        !addedAvailableMetrics || addedAvailableMetrics.length === 0
      }
    >
      <AnimatedList
        onAdd={onAdd}
        onRemove={onRemove}
        onEdit={onEdit}
        items={addedAvailableMetrics}
        RowComponent={AdditionalMetricRow}
        btnText={"Add Optional Metrics"}
        rowProps={{ taken: selectableMetrics }}
        allowDeleteAll
        isDisabled={disabledAddMore}
      />
    </AccordianChartBuilderSidebarBlock>
  );
}

export default ChartBuilderAvailableMetrics;
